import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Minus, Plus, Trash2 } from "lucide-react";
import api from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { Reveal, Overline } from "@/components/Reveal";

export default function Checkout() {
    const { items, subtotal, remove, setQty } = useCart();
    const { user } = useAuth();
    const navigate = useNavigate();
    const [form, setForm] = useState({ name: user?.name || "", email: user?.email || "" });
    const [redeem, setRedeem] = useState(false);
    const [busy, setBusy] = useState(false);

    const points = user?.points || 0;
    const canRedeem = user && points >= 100;
    const memberPct = user && user.membership_status === "active" ? (user.discount_percent || 0) : 0;
    const memberSubtotal = memberPct ? +(subtotal * (1 - memberPct / 100)).toFixed(2) : subtotal;
    const memberSavings = +(subtotal - memberSubtotal).toFixed(2);
    const discount = redeem && canRedeem ? Math.min(Math.floor(points / 20), Math.floor(memberSubtotal - 0.5)) : 0;
    const total = Math.max(0, memberSubtotal - discount);

    const checkout = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            const { data } = await api.post("/checkout/cart", {
                items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity })),
                name: form.name, email: form.email, origin_url: window.location.origin,
                user_id: user ? user.id : null,
                redeem_points: redeem && canRedeem ? points : 0,
            });
            window.location.href = data.checkout_url;
        } catch { toast.error("Could not start checkout."); setBusy(false); }
    };

    const input = "w-full bg-surface border border-gold/20 rounded-xl px-4 py-3 text-cream placeholder:text-faded focus:outline-none focus:ring-1 focus:ring-gold";

    if (items.length === 0)
        return (
            <div className="max-w-md mx-auto px-5 py-32 text-center">
                <h1 className="font-serif text-4xl text-cream">Your basket is empty</h1>
                <Link to="/shop" className="inline-block mt-6 bg-gold text-ink rounded-full px-8 py-3 font-medium">Browse the shop</Link>
            </div>
        );

    return (
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-16">
            <Reveal><Overline>Almost There</Overline><h1 className="mt-3 font-serif text-5xl text-cream">Checkout</h1></Reveal>
            <div className="mt-10 grid md:grid-cols-[1.4fr_1fr] gap-10">
                <div className="space-y-4" data-testid="checkout-items">
                    {items.map((i) => (
                        <div key={i.product_id} className="flex gap-4 p-4 rounded-xl bg-surface border border-gold/10">
                            <img src={i.image} alt={i.name} className="w-20 h-20 object-cover rounded-lg" />
                            <div className="flex-1">
                                <p className="text-cream">{i.name}</p>
                                <p className="text-gold text-sm">${i.price.toFixed(2)}</p>
                                <div className="flex items-center gap-2 mt-2">
                                    <button onClick={() => setQty(i.product_id, i.quantity - 1)} className="w-6 h-6 rounded-full border border-gold/25 text-ash flex items-center justify-center"><Minus className="w-3 h-3" /></button>
                                    <span className="text-cream text-sm w-6 text-center">{i.quantity}</span>
                                    <button onClick={() => setQty(i.product_id, i.quantity + 1)} className="w-6 h-6 rounded-full border border-gold/25 text-ash flex items-center justify-center"><Plus className="w-3 h-3" /></button>
                                    <button onClick={() => remove(i.product_id)} className="ml-auto text-faded hover:text-clay"><Trash2 className="w-4 h-4" /></button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                <form onSubmit={checkout} className="p-7 rounded-2xl bg-surface border border-gold/15 h-fit" data-testid="checkout-form">
                    <h2 className="font-serif text-2xl text-cream mb-5">Your Details</h2>
                    <div className="space-y-4">
                        <input required placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={input} data-testid="checkout-name" />
                        <input required type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={input} data-testid="checkout-email" />
                    </div>

                    {user && (
                        <div className="mt-5">
                            {canRedeem ? (
                                <label className="flex items-center gap-3 text-sm text-ash cursor-pointer" data-testid="checkout-redeem">
                                    <input type="checkbox" checked={redeem} onChange={(e) => setRedeem(e.target.checked)} className="accent-gold w-4 h-4" />
                                    Redeem {points} points for ${Math.min(Math.floor(points / 20), Math.floor(memberSubtotal - 0.5))} off
                                </label>
                            ) : (
                                <p className="text-xs text-faded">Earn points on this order. Redeem once you reach 100 points.</p>
                            )}
                        </div>
                    )}

                    <div className="mt-6 space-y-2 border-t border-gold/10 pt-4 text-sm">
                        <div className="flex justify-between text-ash"><span>Subtotal</span><span>${subtotal.toFixed(2)}</span></div>
                        {memberSavings > 0 && <div className="flex justify-between text-gold"><span>Member discount ({memberPct}%)</span><span>-${memberSavings.toFixed(2)}</span></div>}
                        {discount > 0 && <div className="flex justify-between text-gold"><span>Points discount</span><span>-${discount.toFixed(2)}</span></div>}
                        <div className="flex justify-between text-cream text-lg pt-2"><span>Total</span><span data-testid="checkout-total">${total.toFixed(2)}</span></div>
                    </div>

                    <button disabled={busy} data-testid="checkout-pay" className="mt-6 w-full bg-gold text-ink rounded-full py-3 font-medium hover:bg-goldbright transition-colors active:scale-95 disabled:opacity-60">
                        {busy ? "Redirecting…" : "Pay with Stripe"}
                    </button>
                    <p className="mt-4 text-xs text-faded text-center">Test card 4242 4242 4242 4242 · any future expiry · any CVC</p>
                </form>
            </div>
        </div>
    );
}
